import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Calendar as CalendarIcon, CheckCircle2, XCircle,
  AlertCircle, Banknote, Clock, FileText, Loader2, Plus, Trash2, Eye, Pencil,
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'Pending approval', color: 'bg-amber-100 text-amber-800' },
  approved_awaiting_payment: { label: 'Awaiting payment', color: 'bg-sky-100 text-sky-800' },
  proof_uploaded: { label: 'Proof uploaded — verify', color: 'bg-indigo-100 text-indigo-800' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', color: 'bg-rose-100 text-rose-700' },
  payment_rejected: { label: 'Proof rejected', color: 'bg-orange-100 text-orange-700' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: 'bg-slate-100 text-slate-700' };
  return <Badge className={`${s.color} font-medium`} data-testid={`status-${status}`}>{s.label}</Badge>;
}

export default function AgentVenueBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hoaId: hoaIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const hoaId = hoaIdParam || searchParams.get('building') || user?.hoa_id || user?.assigned_buildings?.[0];

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [venues, setVenues] = useState([]);
  const [profile, setProfile] = useState(null);
  const [filter, setFilter] = useState('');

  // Booking detail / actions
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectPaymentOpen, setRejectPaymentOpen] = useState(false);
  const [approveForm, setApproveForm] = useState({ quoted_amount: '', invoice_number: '', invoice_notes: '', payment_due_date: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Venue management
  const [venueOpen, setVenueOpen] = useState(false);
  const [venueForm, setVenueForm] = useState({ venue_id: null, name: '', description: '', capacity: '', hourly_rate: '', daily_rate: '', min_notice_hours: 24, requires_deposit: false, deposit_amount: '', booking_rules: '', is_active: true });

  // Payment profile
  const [profileForm, setProfileForm] = useState({ account_name: '', bank_name: '', account_number: '', branch_code: '', swift_code: '', account_type: '', reference_format: '', payment_instructions: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  const token = () => localStorage.getItem('session_token');
  const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

  const fetchAll = useCallback(async () => {
    if (!hoaId) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = filter ? `?status=${filter}` : '';
      const [bRes, vRes, pRes] = await Promise.all([
        fetch(`${API_URL}/api/venue-bookings/admin/${hoaId}${qs}`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/venue-bookings/venues/${hoaId}?include_inactive=true`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/venue-bookings/payment-profile/${hoaId}`, { headers: authHeaders(), credentials: 'include' }),
      ]);
      if (bRes.ok) {
        const d = await bRes.json();
        setBookings(d.bookings || []); setStats(d.stats || null);
      } else if (bRes.status === 403) {
        toast.error('Not authorized to manage this building');
      }
      if (vRes.ok) { const d = await vRes.json(); setVenues(d.venues || []); }
      if (pRes.ok) {
        const d = await pRes.json();
        setProfile(d.profile || null);
        if (d.profile) setProfileForm({ ...profileForm, ...d.profile });
      }
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [hoaId, filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = async (b) => {
    setSelected(null); setDetailOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${b.booking_id}`, { headers: authHeaders(), credentials: 'include' });
      if (res.ok) setSelected(await res.json()); else setSelected(b);
    } catch { setSelected(b); }
  };

  const doApprove = async (e) => {
    e?.preventDefault?.();
    if (!approveForm.quoted_amount) { toast.error('Quoted amount required'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${selected.booking_id}/approve`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({
          quoted_amount: Number(approveForm.quoted_amount),
          invoice_number: approveForm.invoice_number || undefined,
          invoice_notes: approveForm.invoice_notes || undefined,
          payment_due_date: approveForm.payment_due_date ? new Date(approveForm.payment_due_date).toISOString() : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Approve failed');
      toast.success(`Approved (invoice ${data.invoice_number})`);
      setApproveOpen(false); setApproveForm({ quoted_amount: '', invoice_number: '', invoice_notes: '', payment_due_date: '' });
      fetchAll(); openDetail({ booking_id: selected.booking_id });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${selected.booking_id}/reject`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Reject failed');
      toast.success('Booking rejected'); setRejectOpen(false); setRejectReason(''); fetchAll(); setDetailOpen(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doVerify = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${selected.booking_id}/verify-payment`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Verify failed');
      toast.success('Payment verified — booking confirmed'); fetchAll(); openDetail({ booking_id: selected.booking_id });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doRejectPayment = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${selected.booking_id}/reject-payment`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Reject failed');
      toast.success('Proof rejected — resident notified'); setRejectPaymentOpen(false); setRejectReason(''); fetchAll(); openDetail({ booking_id: selected.booking_id });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Venue CRUD
  const openVenueDialog = (v = null) => {
    if (v) setVenueForm({ ...v, capacity: v.capacity ?? '', hourly_rate: v.hourly_rate ?? '', daily_rate: v.daily_rate ?? '', deposit_amount: v.deposit_amount ?? '' });
    else setVenueForm({ venue_id: null, name: '', description: '', capacity: '', hourly_rate: '', daily_rate: '', min_notice_hours: 24, requires_deposit: false, deposit_amount: '', booking_rules: '', is_active: true });
    setVenueOpen(true);
  };

  const saveVenue = async (e) => {
    e.preventDefault();
    if (!venueForm.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      const body = {
        hoa_id: hoaId,
        name: venueForm.name,
        description: venueForm.description || undefined,
        capacity: venueForm.capacity ? Number(venueForm.capacity) : undefined,
        hourly_rate: venueForm.hourly_rate ? Number(venueForm.hourly_rate) : undefined,
        daily_rate: venueForm.daily_rate ? Number(venueForm.daily_rate) : undefined,
        min_notice_hours: Number(venueForm.min_notice_hours || 0),
        requires_deposit: !!venueForm.requires_deposit,
        deposit_amount: venueForm.deposit_amount ? Number(venueForm.deposit_amount) : undefined,
        booking_rules: venueForm.booking_rules || undefined,
      };
      let res;
      if (venueForm.venue_id) {
        res = await fetch(`${API_URL}/api/venue-bookings/venues/${venueForm.venue_id}`, {
          method: 'PUT', headers: authHeaders(), credentials: 'include',
          body: JSON.stringify({ ...body, is_active: venueForm.is_active }),
        });
      } else {
        res = await fetch(`${API_URL}/api/venue-bookings/venues`, {
          method: 'POST', headers: authHeaders(), credentials: 'include',
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      toast.success(venueForm.venue_id ? 'Venue updated' : 'Venue created');
      setVenueOpen(false); fetchAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const archiveVenue = async (venue_id) => {
    if (!window.confirm('Archive this venue? Existing bookings remain visible.')) return;
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/venues/${venue_id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Archive failed');
      toast.success('Venue archived'); fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.account_name || !profileForm.bank_name || !profileForm.account_number) {
      toast.error('Account name, bank and account number required'); return;
    }
    setProfileSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/payment-profile/${hoaId}`, {
        method: 'PUT', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({
          account_name: profileForm.account_name,
          bank_name: profileForm.bank_name,
          account_number: profileForm.account_number,
          branch_code: profileForm.branch_code || undefined,
          swift_code: profileForm.swift_code || undefined,
          account_type: profileForm.account_type || undefined,
          reference_format: profileForm.reference_format || undefined,
          payment_instructions: profileForm.payment_instructions || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success('Payment profile saved'); setProfile(data.profile);
    } catch (e) { toast.error(e.message); } finally { setProfileSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  if (!hoaId) return <div className="p-8 max-w-2xl mx-auto"><Card><CardContent className="p-6">No building selected.</CardContent></Card></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Venue Bookings — Admin</h1>
            <p className="text-sm text-slate-500">Approve requests, issue invoices and verify EFT proofs.</p>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {Object.entries(stats).map(([k, v]) => (
              <Card key={k} data-testid={`stat-${k}`}><CardContent className="p-3 text-center"><div className="text-xs uppercase text-slate-500">{k.replace('_', ' ')}</div><div className="text-xl font-semibold">{v}</div></CardContent></Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="bookings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="venues" data-testid="tab-venues">Venues</TabsTrigger>
            <TabsTrigger value="payment-profile" data-testid="tab-payment-profile">EFT Details</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="space-y-3">
            <div className="flex items-center gap-2">
              <Select value={filter || 'all'} onValueChange={(v) => setFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-56" data-testid="status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchAll}>Refresh</Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Venue</TableHead>
                      <TableHead>Resident</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No bookings</TableCell></TableRow>}
                    {bookings.map((b) => (
                      <TableRow key={b.booking_id} data-testid={`booking-row-${b.booking_id}`}>
                        <TableCell className="font-medium">{b.venue_name}</TableCell>
                        <TableCell>{b.resident_name}<div className="text-xs text-slate-500">Unit {b.unit_number || '—'}</div></TableCell>
                        <TableCell className="whitespace-nowrap">{format(new Date(b.start_time), 'PP p')}</TableCell>
                        <TableCell><StatusBadge status={b.status} /></TableCell>
                        <TableCell>{b.quoted_amount != null ? `R${Number(b.quoted_amount).toFixed(2)}` : '—'}</TableCell>
                        <TableCell><Button variant="ghost" size="sm" onClick={() => openDetail(b)} data-testid={`open-booking-${b.booking_id}`}><Eye className="w-4 h-4" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="venues" className="space-y-3">
            <div className="flex justify-end"><Button onClick={() => openVenueDialog()} data-testid="new-venue-btn"><Plus className="w-4 h-4 mr-1" /> New Venue</Button></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {venues.length === 0 && <Card className="sm:col-span-2 lg:col-span-3"><CardContent className="p-8 text-center text-slate-500">No venues yet.</CardContent></Card>}
              {venues.map((v) => (
                <Card key={v.venue_id} data-testid={`venue-card-${v.venue_id}`} className={!v.is_active ? 'opacity-60' : ''}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="text-base">{v.name}</CardTitle>
                        {v.capacity && <CardDescription>Capacity {v.capacity}</CardDescription>}
                      </div>
                      {!v.is_active && <Badge variant="secondary">Archived</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-slate-600">
                    {v.description && <p>{v.description}</p>}
                    {v.hourly_rate != null && <p>Hourly: R{Number(v.hourly_rate).toFixed(2)}</p>}
                    {v.daily_rate != null && <p>Daily: R{Number(v.daily_rate).toFixed(2)}</p>}
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" onClick={() => openVenueDialog(v)} data-testid={`edit-venue-${v.venue_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                      {v.is_active && <Button size="sm" variant="ghost" onClick={() => archiveVenue(v.venue_id)} data-testid={`archive-venue-${v.venue_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="payment-profile">
            <Card>
              <CardHeader>
                <CardTitle>EFT Bank Details</CardTitle>
                <CardDescription>Shown to residents only after their booking is approved.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={saveProfile} className="space-y-3 max-w-2xl">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Account name *</Label><Input value={profileForm.account_name} onChange={(e) => setProfileForm({ ...profileForm, account_name: e.target.value })} data-testid="profile-account-name" /></div>
                    <div><Label>Bank *</Label><Input value={profileForm.bank_name} onChange={(e) => setProfileForm({ ...profileForm, bank_name: e.target.value })} data-testid="profile-bank-name" /></div>
                    <div><Label>Account number *</Label><Input value={profileForm.account_number} onChange={(e) => setProfileForm({ ...profileForm, account_number: e.target.value })} data-testid="profile-account-number" /></div>
                    <div><Label>Branch code</Label><Input value={profileForm.branch_code} onChange={(e) => setProfileForm({ ...profileForm, branch_code: e.target.value })} data-testid="profile-branch-code" /></div>
                    <div><Label>SWIFT</Label><Input value={profileForm.swift_code} onChange={(e) => setProfileForm({ ...profileForm, swift_code: e.target.value })} data-testid="profile-swift" /></div>
                    <div><Label>Account type</Label><Input placeholder="Cheque / Savings" value={profileForm.account_type} onChange={(e) => setProfileForm({ ...profileForm, account_type: e.target.value })} data-testid="profile-account-type" /></div>
                  </div>
                  <div><Label>Payment instructions</Label><Textarea rows={3} value={profileForm.payment_instructions} onChange={(e) => setProfileForm({ ...profileForm, payment_instructions: e.target.value })} data-testid="profile-instructions" /></div>
                  <Button type="submit" disabled={profileSaving} data-testid="save-payment-profile-btn">{profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save EFT Details'}</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking detail */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl" data-testid="booking-detail-dialog">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{selected.venue_name} <StatusBadge status={selected.status} /></DialogTitle>
                <DialogDescription>Booking #{selected.booking_id.slice(-8)} by {selected.resident_name} (Unit {selected.unit_number || '—'})</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500">Start:</span> {format(new Date(selected.start_time), 'PPp')}</div>
                  <div><span className="text-slate-500">End:</span> {format(new Date(selected.end_time), 'PPp')}</div>
                  <div><span className="text-slate-500">Purpose:</span> {selected.purpose}</div>
                  {selected.expected_guests ? <div><span className="text-slate-500">Guests:</span> {selected.expected_guests}</div> : null}
                  {selected.contact_phone && <div><span className="text-slate-500">Phone:</span> {selected.contact_phone}</div>}
                </div>
                {selected.special_requirements && <div><span className="text-slate-500">Requirements:</span> {selected.special_requirements}</div>}

                {selected.quoted_amount != null && (
                  <div className="rounded-md border bg-slate-50 p-4 text-sm space-y-1">
                    <div className="font-medium">Invoice</div>
                    <div>No: <span className="font-mono">{selected.invoice_number}</span></div>
                    <div>Amount: R{Number(selected.quoted_amount).toFixed(2)}</div>
                    {selected.payment_due_date && <div>Due: {format(new Date(selected.payment_due_date), 'PP')}</div>}
                  </div>
                )}

                {selected.payment_proof_url && (
                  <div className="rounded-md border p-3 text-sm">
                    <div className="font-medium mb-1 flex items-center gap-1"><FileText className="w-4 h-4" /> Payment proof</div>
                    <a href={`${API_URL}${selected.payment_proof_url}`} target="_blank" rel="noreferrer" className="underline text-indigo-700">Open proof ({selected.payment_proof_original_name})</a>
                    {selected.payment_reference && <div>Ref: <span className="font-mono">{selected.payment_reference}</span></div>}
                    {selected.paid_amount && <div>Amount paid: R{Number(selected.paid_amount).toFixed(2)}</div>}
                    {selected.paid_at && <div>Paid at: {format(new Date(selected.paid_at), 'PPp')}</div>}
                  </div>
                )}

                {selected.status_history?.length > 0 && (
                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer">Status history ({selected.status_history.length})</summary>
                    <ul className="mt-1 space-y-1">
                      {selected.status_history.map((h, i) => (
                        <li key={i}>• <span className="font-mono">{h.status}</span> by {h.by_name || h.by_role} at {format(new Date(h.at), 'PP p')}{h.reason ? ` — ${h.reason}` : ''}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                {selected.status === 'pending' && (
                  <>
                    <Button onClick={() => { setApproveForm({ ...approveForm, invoice_number: `VB-${selected.booking_id.slice(-8).toUpperCase()}` }); setApproveOpen(true); }} data-testid="approve-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve & Issue invoice</Button>
                    <Button variant="destructive" onClick={() => setRejectOpen(true)} data-testid="reject-btn"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                  </>
                )}
                {selected.status === 'proof_uploaded' && (
                  <>
                    <Button onClick={doVerify} disabled={busy} data-testid="verify-payment-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Verify & Confirm</Button>
                    <Button variant="destructive" onClick={() => setRejectPaymentOpen(true)} data-testid="reject-payment-btn"><AlertCircle className="w-4 h-4 mr-1" /> Reject proof</Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </>
          ) : <Loader2 className="w-6 h-6 animate-spin mx-auto" />}
        </DialogContent>
      </Dialog>

      {/* Approve */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve & Issue Invoice</DialogTitle>
            <DialogDescription>Resident will see EFT bank details after approval.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doApprove} className="space-y-3">
            <div><Label>Quoted amount (R) *</Label><Input type="number" step="0.01" min="0.01" value={approveForm.quoted_amount} onChange={(e) => setApproveForm({ ...approveForm, quoted_amount: e.target.value })} data-testid="quoted-amount-input" /></div>
            <div><Label>Invoice number</Label><Input value={approveForm.invoice_number} onChange={(e) => setApproveForm({ ...approveForm, invoice_number: e.target.value })} data-testid="invoice-number-input" /></div>
            <div><Label>Payment due date</Label><Input type="date" value={approveForm.payment_due_date} onChange={(e) => setApproveForm({ ...approveForm, payment_due_date: e.target.value })} data-testid="due-date-input" /></div>
            <div><Label>Notes</Label><Textarea rows={3} value={approveForm.invoice_notes} onChange={(e) => setApproveForm({ ...approveForm, invoice_notes: e.target.value })} data-testid="invoice-notes-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="submit-approve-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject booking */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject Booking</DialogTitle><DialogDescription>Resident will be notified.</DialogDescription></DialogHeader>
          <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason" data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} disabled={busy} data-testid="submit-reject-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject proof */}
      <Dialog open={rejectPaymentOpen} onOpenChange={setRejectPaymentOpen}>
        <DialogContent className="max-w-md" data-testid="reject-payment-dialog">
          <DialogHeader><DialogTitle>Reject Payment Proof</DialogTitle><DialogDescription>Resident can upload a corrected proof.</DialogDescription></DialogHeader>
          <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Why is the proof not acceptable?" data-testid="reject-payment-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectPaymentOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doRejectPayment} disabled={busy} data-testid="submit-reject-payment-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject Proof'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Venue dialog */}
      <Dialog open={venueOpen} onOpenChange={setVenueOpen}>
        <DialogContent className="max-w-lg" data-testid="venue-dialog">
          <DialogHeader><DialogTitle>{venueForm.venue_id ? 'Edit Venue' : 'New Venue'}</DialogTitle></DialogHeader>
          <form onSubmit={saveVenue} className="space-y-3">
            <div><Label>Name *</Label><Input value={venueForm.name} onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })} data-testid="venue-name-input" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={venueForm.description} onChange={(e) => setVenueForm({ ...venueForm, description: e.target.value })} data-testid="venue-description-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Capacity</Label><Input type="number" min="0" value={venueForm.capacity} onChange={(e) => setVenueForm({ ...venueForm, capacity: e.target.value })} data-testid="venue-capacity-input" /></div>
              <div><Label>Hourly (R)</Label><Input type="number" step="0.01" value={venueForm.hourly_rate} onChange={(e) => setVenueForm({ ...venueForm, hourly_rate: e.target.value })} data-testid="venue-hourly-input" /></div>
              <div><Label>Daily (R)</Label><Input type="number" step="0.01" value={venueForm.daily_rate} onChange={(e) => setVenueForm({ ...venueForm, daily_rate: e.target.value })} data-testid="venue-daily-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div><Label>Min notice (hours)</Label><Input type="number" min="0" value={venueForm.min_notice_hours} onChange={(e) => setVenueForm({ ...venueForm, min_notice_hours: e.target.value })} data-testid="venue-min-notice-input" /></div>
              <div className="flex items-center gap-2 mt-6"><Switch checked={venueForm.requires_deposit} onCheckedChange={(v) => setVenueForm({ ...venueForm, requires_deposit: v })} data-testid="venue-requires-deposit" /> <Label>Requires deposit</Label></div>
            </div>
            {venueForm.requires_deposit && (
              <div><Label>Deposit amount (R)</Label><Input type="number" step="0.01" value={venueForm.deposit_amount} onChange={(e) => setVenueForm({ ...venueForm, deposit_amount: e.target.value })} data-testid="venue-deposit-input" /></div>
            )}
            <div><Label>Booking rules</Label><Textarea rows={3} value={venueForm.booking_rules} onChange={(e) => setVenueForm({ ...venueForm, booking_rules: e.target.value })} data-testid="venue-rules-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVenueOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="save-venue-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
